import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { DateTime } from 'luxon';

import { SharedSettings } from 'projects/shared/src/lib/models/shared-settings';
import { url } from 'projects/shared/src/lib/helpers/url';

import { PalletReclassificationItem } from '../models/pallet-reclassification';
import { ShiftContextService } from './shift-context.service';

@Injectable({
    providedIn: 'root'
})
export class PalletReclassificationService {

    private _currentPalletReclassificationList: BehaviorSubject<PalletReclassificationItem[]>;
    subscriptions: Subscription[];
    shiftContextId: number;

    constructor(private http: HttpClient, private settings: SharedSettings, private shiftContextService: ShiftContextService) {
        this._currentPalletReclassificationList = new BehaviorSubject<PalletReclassificationItem[]>(null);
    }

    load() {
        this.subscriptions = [
            combineLatest([this.shiftContextService.CurrentContext]).subscribe(([context]) => {
                this.shiftContextId = context.id;

                this.GetReclassList(this.shiftContextId);
            })
        ];
    }

    unload() {
        this._currentPalletReclassificationList.next(null);
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    GetReclassList(shiftContextId: number): void {
        if (shiftContextId) {
            this.http.get<PalletReclassificationItem[]>(this.settings.apiBaseUrl + url`palletreclassificationtransactions?workedShiftId=${shiftContextId}`)
                .subscribe(currentPalletReclassificationList => {
                    this._currentPalletReclassificationList.next(currentPalletReclassificationList);
                });
        }
    }

    get CurrentPalletReclassificationList(): Observable<PalletReclassificationItem[]> {
        return this._currentPalletReclassificationList.asObservable().pipe(filter(palletsReclassList => palletsReclassList !== null));
    }

    createReclass(reclass: PalletReclassificationItem): Observable<PalletReclassificationItem> {
        return this.http.post<PalletReclassificationItem>(this.settings.apiBaseUrl +
            url`palletreclassificationtransactions`, reclass)
            .pipe(tap(newReclass => {
                const palletReclass = this._currentPalletReclassificationList.value;
                palletReclass.push(newReclass);
                this._currentPalletReclassificationList.next(palletReclass);
            }));
    }

    updateReclass(reclass: PalletReclassificationItem): Observable<PalletReclassificationItem> {
        if (reclass) {
            return this.http.put<PalletReclassificationItem>(this.settings.apiBaseUrl +
                url`palletreclassificationtransactions`, reclass)
                .pipe(tap(updatedEntry => {
                    const palletReclass = this._currentPalletReclassificationList.value;
                    const oldEntry = palletReclass.find(x => x.id === updatedEntry.id);
                    palletReclass.splice(palletReclass.indexOf(oldEntry),
                        1,
                        updatedEntry);
                    this._currentPalletReclassificationList.next(palletReclass);
                }));
        } else {
            return of();
        }
    }

    deleteReclass(id: number) {
        if (id) {
            return this.http.delete(this.settings.apiBaseUrl +
                url`palletreclassificationtransactions/${id}`)
                .pipe(tap(_ => {
                    const palletReclass = this._currentPalletReclassificationList.value;
                    const deletedEntry = palletReclass.find(x => x.id === id);
                    palletReclass.splice(palletReclass.indexOf(deletedEntry), 1);
                    this._currentPalletReclassificationList.next(palletReclass);
                }));
        }
    }
}
